import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/Button';
import Input from '../../components/Input';
import './styles.css';

const Login: React.FC = () => {
  const { login, isAuthenticated, loading, confirmEmail } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    // Redirect when authenticated, regardless of email confirmation status
    if (isAuthenticated && !loading) {
      // safe redirect
      navigate('/vitrine');
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    const message = searchParams.get('message');
    if (message === 'password_updated') {
      setSuccess('Senha redefinida com sucesso! Faça login com sua nova senha.');
    } else if (message === 'email_confirmed') {
      setSuccess('E-mail confirmado com sucesso! Faça login para continuar.');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const { error } = await login(email, password);
      if (error) {
        const errorMessage = (error as any)?.message || 'Erro ao fazer login';
        
        // Tratar erro de e-mail não confirmado
        if (errorMessage.includes('Email not confirmed') || (error as any)?.code === 'email_not_confirmed') {
          console.log('Detectado erro de e-mail não confirmado. Tentando confirmar e-mail automaticamente...');
          
          try {
            // Tentar confirmar o email automaticamente
            await confirmEmail(email);
            console.log('E-mail confirmado com sucesso. Tentando login novamente...');
            
            // Tentar login novamente após confirmar o email
            const { error: loginError } = await login(email, password);
            if (loginError) {
              console.error('Erro no segundo login após confirmar e-mail:', loginError);
              if ((loginError as any)?.message?.includes('Invalid login credentials')) {
                setError('E-mail ou senha inválidos');
              } else {
                setError('Erro ao fazer login. Tente novamente.');
              }
            }
          } catch (confirmError) {
            console.error('Erro ao confirmar e-mail:', confirmError);
            setError('Não foi possível confirmar seu e-mail automaticamente. Entre em contato com o suporte.');
          }
        } else if (errorMessage.includes('Invalid login credentials')) {
          setError('E-mail ou senha inválidos');
        } else {
          setError('Erro ao fazer login. Tente novamente.');
        }
      }
    } catch (err: any) {
      setError('Ocorreu um erro inesperado. Tente novamente mais tarde.');
      console.error('Login error:', err);
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    const popupElement = document.querySelector('.popup-class');
    const toggleButton = document.querySelector('.popup-toggle');
    if (
      popupElement &&
      !popupElement.contains(event.target as Node) &&
      toggleButton &&
      !toggleButton.contains(event.target as Node)
    ) {
      setIsPopupOpen(false);
    }
  };

  useEffect(() => {
    if (isPopupOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isPopupOpen]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  return (
    <div className={`body-wrapper auth-wrapper ${darkMode ? 'dark-mode' : ''}`}>
      <div className="d-flex flex-grow flex-column-reverse flex-sm-row w-100">
        <div className="auth-bg col-sm-6">
          <p className="auth-credits">
            <small>&copy; 2025 - Trading<br/>Todos os direitos reservados.</small>
          </p>
        </div>

        <div className="auth-panel-wrapper col">
          <div className="auth-panel">
            <img 
              src="/logo-trading.png" 
              className="auth-logo mx-auto" 
              alt="Trading Logo"
            />

            <div className="auth-header text-center mb-4">
              <h4>Acesse sua conta</h4>
              <p>
                Ainda não tem uma conta?{' '}
                <Link to="/cadastrar" className="text-primary">Cadastre-se agora</Link>
              </p>
            </div>

            {success && <div className="alert alert-success">{success}</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {/* Adicionando rolagem em telas pequenas */}
            <form onSubmit={handleSubmit} className="form-scroll">
              <Input
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu e-mail"
                autoComplete="username"
                required
              />

              <div className="py-3">
                <Input
                  label="Senha"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  autoComplete="current-password"
                  required
                />
              </div>

              <Button 
                type="submit"
                variant="auth"
                fullWidth
                disabled={loading}
                className="mt-3"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Entrando...
                  </>
                ) : 'Entrar'}
              </Button>

              <p className="text-center mt-3">
                <Link to="/esqueci-senha" className="text-decoration-none">
                  Esqueceu sua senha?
                </Link>
              </p>
            </form>

            {/* Removendo o botão de abrir popup */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
